/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Component, ElementRef, EventEmitter, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { PidxBannerService } from '@myprofile-ui/pidx-ui-components'
import { PidxScrollToService, WINDOW } from '@myprofile-ui/pidx-utils'
import { TranslateService } from '@ngx-translate/core'
import { Store } from '@ngxs/store'
import { PinCodeState, PinCodeChangeEventDetail, ToastManager } from '@porsche-design-system/components-angular'
import { lastValueFrom } from 'rxjs'
import { ConfirmationCodeStatus } from '../../enums/pidx-confirmation-code-status'
import { DeletePhone } from '../../store/editor.actions'
import { GetPhones } from '../../store/list.actions'
import { PhoneCommunicationDto } from '../../../../../../apps/myprofile-ui/src/app/services/phone/phone.service'
import { CommunicationVerificationService } from '../../../../../../apps/myprofile-ui/src/app/services/communication-verification/communicationVerification.service'
import { HttpErrorResponse } from '@angular/common/http'

@Component({
  selector: 'myprofile-phone-verification',
  templateUrl: './myprofile-phone-verification.component.html',
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfilePhoneVerificationComponent implements OnInit {
  private notificationsService = inject(PidxBannerService)
  private toastManager = inject(ToastManager)
  private communicationVerificationService = inject(CommunicationVerificationService)
  private _translateService = inject(TranslateService)
  private store = inject(Store)
  private pidxScrollToService = inject(PidxScrollToService)
  private element = inject(ElementRef)
  private window = inject<Window>(WINDOW)

  @Output() cancelEvent = new EventEmitter()
  @Output() verified = new EventEmitter()

  public isDisabled = false
  public phoneNumberBeingVerified = this.store.selectSnapshot((state) => state.phoneWidget.phoneNumberBeingVerified)

  public phoneIdBeingEdited = this.store.selectSnapshot((state) => state.phoneWidget.phoneIdBeingEdited)
  public confirmationCodeStatus: ConfirmationCodeStatus = ConfirmationCodeStatus.CODE_STATUS_DEFAULT
  public confirmationCodeForm = new UntypedFormGroup({})
  public waitingTime = 30
  public messages: string[] = []
  public messageState: PinCodeState = 'none'
  public country = this.store.selectSnapshot((state) => state.phoneWidget.country)
  protected readonly ConfirmationCodeStatus = ConfirmationCodeStatus

  private locale = this.store.selectSnapshot((state) => state.phoneWidget.locale)
  private phoneNumberObject: PhoneCommunicationDto | undefined

  ngOnInit() {
    this.window.setTimeout(() => {
      this.window.document.getElementById('requestAndInsertHeadline')?.focus()
    }, 0)
    this.requestConfirmationCode()
  }

  async requestConfirmationCode() {
    this.phoneNumberObject = this.store.selectSnapshot((state) => {
      return state.phoneWidget.phones.find((phone: PhoneCommunicationDto) => phone.number === this.phoneNumberBeingVerified.number)
    })

    try {
      this.window.setTimeout(() => {
        this.pidxScrollToService.scrollToElement(this.element.nativeElement, 24)
      }, 0)
      await lastValueFrom(this.communicationVerificationService.sendSmsVerification(this.country, this.locale, this.phoneNumberObject!.id))
      this.toastManager.addMessage({ text: this._translateService.instant('pidp.verifyMobile.verificationCodeSend'), state: 'success' })
    } catch (error) {
      const errorKey = (error as HttpErrorResponse)?.error?.collection?.error?.message
      this.notificationsService.addPageBlockingError(this._translateService.instant(errorKey))
      this.isDisabled = true
    }
  }

  async validateConfirmationCode(e: CustomEvent<PinCodeChangeEventDetail>) {
    if (!e.detail.isComplete) return
    this.confirmationCodeStatus = ConfirmationCodeStatus.CODE_STATUS_VALIDATION_PENDING
    this.messages = []

    const confirmationCode = e.detail.value

    try {
      await lastValueFrom(
        this.communicationVerificationService.verifyMobileWithSmsPin(this.country, this.locale, this.phoneNumberObject!.id, {
          smsPin: confirmationCode,
        }),
      )
      this.messageState = 'success'
      this.verified.emit()

      await new Promise<void>((resolve) => setTimeout(() => resolve(), 1000))
      this.toastManager.addMessage({ text: this._translateService.instant('pidpaw.editPhone.text.verification.success'), state: 'success' })
      this.store.dispatch(new GetPhones())
      this.cancelEvent.emit()
    } catch (error) {
      this.confirmationCodeStatus = ConfirmationCodeStatus.CODE_STATUS_DEFAULT
      const errorKey = (error as HttpErrorResponse)?.error?.collection?.error?.message
      if (errorKey !== null) {
        this.messages = [this._translateService.instant(errorKey)]
      }
      this.messageState = 'error'
    }
  }

  public cancel() {
    this.store.dispatch(new DeletePhone(this.phoneNumberBeingVerified))
    this.cancelEvent.emit()
  }
}
